<template>
  <a-form-model
    class="attribute-list-record"
    :labelCol="{ span: 0, offset: 0 }"
    :wrapperCol="{ span: 24, offset: 0 }"
    :colon="false"
    :model="form"
  >
    <slot name="name">
      <div class="attribute-list-record__name">
        <span
          v-if="props.attribute.is_required"
          class="attribute-list-record__required"
          aria-hidden="true"
        >
          *
        </span>
        {{ getAttributeLabel(props.attribute) }}
      </div>
    </slot>

    <div
      v-if="props.attribute.value_type === 'NUMERIC'"
      class="attribute-list-record__value attribute-list-record__numeric-value"
    >
      <a-form-model-item
        :validateStatus="validateStatus"
        :help="fieldValidationMessage"
        :labelCol="{ span: 0, offset: 0 }"
        :wrapperCol="{ span: 24, offset: 0 }"
      >
        <a-input-number
          v-model="form.value"
          :disabled="props.disabled"
          style="width: 100%"
          :placeholder="`${$t('offers.create_form_placeholders.input_value')}`"
          @change="onValueChanged"
        />
      </a-form-model-item>
      <a-form-model-item>
        <InfiniteScrollSelect
          :value="form.unit"
          :isDisabled="props.disabled"
          :customRequest="fetchAttributeUnits"
          :customRequestParams="{ code: props.attribute.code }"
          :defaultOptions="defaultUnitOptions"
          :placeholder="`${$t('offers.create_form_placeholders.unit')}`"
          valueKey="oid"
          labelKey="name"
          :showSearch="false"
          onFocusFetch
          :allowClear="!props.attribute.default_unit"
          @change="onUnitChange"
        />
      </a-form-model-item>
    </div>

    <a-form-model-item
      v-else
      class="attribute-list-record__value"
      :validateStatus="validateStatus"
      :help="fieldValidationMessage"
      :labelCol="{ span: 0, offset: 0 }"
      :wrapperCol="{ span: 24, offset: 0 }"
    >
      <a-input
        v-model.trim="form.value"
        :disabled="props.disabled"
        :placeholder="`${$t('offers.create_form_placeholders.input_value')}`"
        @change="onValueChanged"
      />
    </a-form-model-item>

    <a-form-model-item
      v-if="showDeleteButton"
      class="attribute-list-record__action"
    >
      <a-button
        icon="delete"
        type="dashed"
        shape="circle"
        :aria-label="$t('common.delete')"
        @click="removeAttribute"
      />
    </a-form-model-item>
  </a-form-model>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive, ref } from "vue"
import { TranslateResult } from "vue-i18n"

import InfiniteScrollSelect from "@/components/Common/InfiniteScrollSelect/index.vue"
import i18n from "@/i18n"
import { fetchAttributeUnits } from "@/modules/offers/api/productModeration"
import {
  ProductModerationAttribute,
  ProductModerationAttributeUnit
} from "@/modules/offers/types/offers"

import { getAttributeLabel, getAttributeValueLabel } from "../../utils/attributeLabels"
import { hasAttributeValue } from "../../utils/productAttributes"

type Props = {
  attribute: ProductModerationAttribute
  showDeleteButton?: boolean
  showValidation?: boolean
  disabled?: boolean
}
type Emits = {
  (event: "remove", attribute: ProductModerationAttribute): void
  (event: "change", attribute: ProductModerationAttribute): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const form = reactive({
  value: undefined,
  unit: undefined
})

const validateStatus = computed<string>(() => {
  if (!props.attribute.is_required) return "success"

  if (!props.showValidation) return "success"

  return hasAttributeValue(props.attribute) ? "success" : "error"
})
const fieldValidationMessage = computed<TranslateResult>(() =>
  validateStatus.value === "error" ? i18n.t("validations.required_field") : ""
)

const removeAttribute = () => {
  emit("remove", props.attribute)
}
const onValueChanged = () => {
  emit("change", {
    ...props.attribute,
    value: [
      {
        value: form.value,
        unit: form.unit
          ? defaultUnitOptions.value.find((item) => item.oid === form.unit) || { oid: form.unit }
          : undefined
      }
    ]
  })
}

// UNITS
const defaultUnitOptions = ref([])

const onUnitChange = (value: string) => {
  form.unit = value
  onValueChanged()
}
const setUnit = (unit: ProductModerationAttributeUnit) => {
  if (unit) {
    defaultUnitOptions.value.push(unit)
    form.unit = unit.oid
  }
}
const setupAttribute = () => {
  const [currentValue] = props.attribute?.value || []

  if (currentValue) {
    setUnit(currentValue.unit || props.attribute.default_unit)

    form.value = getAttributeValueLabel(currentValue)
  } else if (props.attribute.default_unit) {
    setUnit(props.attribute.default_unit)
  }
}

onMounted(setupAttribute)
</script>

<style lang="scss" scoped>
.attribute-list-record {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 40px;
  padding: 4px 0;

  &__name {
    flex: 0 1 440px;
    min-width: 0;
    padding-left: 8px;
    line-height: 22px;
    overflow-wrap: anywhere;
  }

  &__required {
    margin-right: 4px;
    color: $red-color-5;
  }

  &__value {
    flex: 0 1 285px;
    min-width: 0;
    margin: 0;
  }

  &__action {
    flex: 0 0 32px;
    margin: 0;
  }

  ::v-deep .ant-form-item-control {
    line-height: 32px;
  }

  ::v-deep .ant-form-item-control-wrapper {
    width: 100%;
  }

  ::v-deep .ant-form-item-children {
    display: flex;
    align-items: flex-start;
  }

  &__numeric-value {
    display: grid;
    grid-template-columns: minmax(0, 285px) 85px;
    flex-basis: 390px;
    gap: 16px;

    & > .ant-form-item {
      min-width: 0;
      margin: 0;
    }
  }
}
</style>
